import { supabase } from '@/lib/supabaseClient'
import { requireOpenWorldSession } from '@/api/adapters/supabase/open-world-auth'
import { isOpenWorldAttemptUuid } from '@/lib/openWorlds/attemptPersistence'
import { OpenWorldPersistenceError, validateOpenWorldBaseline } from '@/lib/openWorlds/persistence'
import type { GetOpenWorldCanonInputRequest, OpenWorldCanonInput } from '@/lib/openWorlds/canon'

export function mapOpenWorldCanonInput(value: unknown): OpenWorldCanonInput {
  const fail = (): never => { throw new OpenWorldPersistenceError('backend', 'Malformed captured World canon.') }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return fail()
  const row = value as Record<string, unknown>
  if (Object.keys(row).sort().join(',') !== 'entries,mode,source' ||
    !['snapshot', 'live'].includes(row.mode as string) || (row.mode === 'live' && row.source === null)) return fail()
  const checked = validateOpenWorldBaseline({
    schemaVersion: row.source === null ? 1 : 2,
    title: 'Captured World', summary: '', initialMessage: 'Opening', seedEntries: row.entries,
    ...(row.source === null ? {} : { source: row.source }),
  })
  if (!checked.success) return fail()
  return structuredClone(value) as OpenWorldCanonInput
}

export async function getOpenWorldCanonInput(request: GetOpenWorldCanonInputRequest): Promise<OpenWorldCanonInput> {
  if (Object.keys(request).length !== 1 || !isOpenWorldAttemptUuid(request.attemptId ?? request.noteId)) {
    throw new OpenWorldPersistenceError('invalid_input', 'Choose one saved attempt or note.')
  }
  await requireOpenWorldSession()
  const { data, error } = await supabase.rpc('rpc_get_open_world_canon_input_v1', {
    p_attempt_id: request.attemptId ?? null, p_note_id: request.noteId ?? null,
  })
  if (error) throw new OpenWorldPersistenceError(error.message.includes('OW_NOT_FOUND') ? 'not_found' : 'backend',
    'The captured World canon could not be loaded.', { cause: error })
  return mapOpenWorldCanonInput(data)
}
